// Order Details "Readiness Checklist" — the container (ticket 86d385wc0, Phase 1).
//
// Renders the SO form field-groups as a single-open accordion of status-badged
// cards. Statuses come from the one sectionStatus model (the spine), so the
// scannable left status column, the (Phase-2) readiness bar, and (Phase-2)
// submit gating can never disagree.
//
// Scope: form field-groups ONLY. The line-items table / extraction / editing
// workspace are untouched (user preference) and live outside this component.

import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { useFormContext, Controller, useWatch, type Control } from 'react-hook-form';
import {
  type SalesOrderFormData,
  draftToFormData,
} from '../../schemas/salesOrderFormSchema';

// Boolean-valued form keys — constrains ControlledFlag so field.value types as boolean.
type BooleanFieldName = {
  [K in keyof SalesOrderFormData]: SalesOrderFormData[K] extends boolean ? K : never;
}[keyof SalesOrderFormData];
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Lock, Plus, X, Receipt } from 'lucide-react';
import { cn } from '@/lib/utils';
import type {
  FirmOfferDetailDto,
  SalesOrderDto,
  ShippingAddressInput,
} from '../../types/salesOrder.types';
import { useSalesOrderStore } from '../../store/salesOrderStore';
import { referenceDataService } from '../../services/referenceDataService';
import { aggregateRebarPricing, kgToMt } from '../../utils/pricing';
import { totalLineWeightKg } from '../../utils/lineWeight';
import {
  combinedOrderTotal,
  computeSoFreight,
  freightBillingNeedsPrompt,
  resolveIncludeFreightLine,
  SeparateBilling,
} from '../../utils/freight';
import { Truck } from 'lucide-react';
import { ReferenceDataCombobox } from '../ReferenceDataCombobox';
import { SearchableSelect } from '../SearchableSelect';
import { ShippingAddressPicker } from '../ShippingAddressPicker';
import { AddressPreview } from '../AddressPreview';
import { AddressCard } from '../AddressCard';
import { PerGradeBalanceChips } from '../PerGradeBalanceChips';
import { ReadinessCard } from './ReadinessCard';
import { deriveSectionStatuses, type SectionDescriptor, type SectionKey } from './sectionStatus';

interface Props {
  draft: SalesOrderDto;
  firmOffer: FirmOfferDetailDto | null;
  /** FO detail still loading (reopen/fresh-pick re-fetch). FO-derived bodies show
   *  skeletons instead of their no-FO placeholders while true (86d3a1q88). */
  isLoadingFirmOffer?: boolean;
  readOnly?: boolean;
  /** When true, j/k move between cards and Enter focuses the open card body. */
  keyboardActive?: boolean;
  /** Section descriptors computed once by the parent (the readiness bar uses the same
   *  set), passed in so we don't re-derive them a second time per keystroke (86d3afaup).
   *  Optional: callers that omit it fall back to a local derivation. Not forwarded to
   *  the body components. */
  sections?: SectionDescriptor[];
}

export interface ReadinessChecklistHandle {
  /** Open a section and scroll it into view (used by the readiness bar). */
  openSection: (key: SectionKey) => void;
}

// Waived Freight is NetSuite customlist563 — values are NS internal IDs.
const WAIVED_FREIGHT_OPTIONS = [
  { id: '2', label: 'No' },
  { id: '1', label: 'Yes' },
];

export const SalesOrderReadinessChecklist = forwardRef<ReadinessChecklistHandle, Props>(
  function SalesOrderReadinessChecklist(
    { draft, firmOffer, isLoadingFirmOffer, readOnly, keyboardActive, sections: sectionsProp },
    ref,
  ) {
    // Prefer the parent-computed sections (single source — the readiness bar derives the
    // same set, so we avoid a second deriveSectionStatuses walk per keystroke, 86d3afaup).
    // `??` short-circuits, so the local derivation never runs when the prop is supplied.
    const sections =
      sectionsProp ?? deriveSectionStatuses(draft, Boolean(draft.linkedFirmOfferInternalId));

    // Single-open accordion. Default to the first section that needs attention so
    // the coordinator lands on actionable work; fall back to the first card.
    const [openKey, setOpenKey] = useState<SectionKey | null>(() => {
      const firstAttention = sections.find((s) => s.kind === 'attention');
      return firstAttention?.key ?? 'purchaseOrder';
    });

    function openAndScroll(key: SectionKey) {
      setOpenKey(key);
      requestAnimationFrame(() => {
        document
          .getElementById(`so-section-${key}`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    }

    useImperativeHandle(ref, () => ({ openSection: openAndScroll }), []);

    function toggle(key: SectionKey) {
      setOpenKey((prev) => (prev === key ? null : key));
    }

    // j/k move between cards; Enter focuses the open card body. Ignored while
    // typing in a field so it never fights form input.
    useEffect(() => {
      if (!keyboardActive) return;
      function onKey(e: KeyboardEvent) {
        if (e.key !== 'j' && e.key !== 'k' && e.key !== 'Enter') return;
        const el = document.activeElement as HTMLElement | null;
        const tag = el?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el?.isContentEditable) {
          return;
        }
        const order = sections.map((s) => s.key);
        if (e.key === 'Enter') {
          if (!openKey) return;
          e.preventDefault();
          const body = document.getElementById(`so-section-${openKey}`);
          body?.querySelector<HTMLElement>('input, textarea, select, button, [tabindex]')?.focus();
          return;
        }
        e.preventDefault();
        const idx = openKey ? order.indexOf(openKey) : -1;
        const nextIdx =
          e.key === 'j'
            ? Math.min(order.length - 1, idx + 1)
            : Math.max(0, (idx < 0 ? order.length : idx) - 1);
        openAndScroll(order[nextIdx]);
      }
      window.addEventListener('keydown', onKey);
      return () => window.removeEventListener('keydown', onKey);
    }, [keyboardActive, sections, openKey]);

    return (
      <div className="space-y-2">
        {sections.map((section) => (
          <ReadinessCard
            key={section.key}
            domId={`so-section-${section.key}`}
            title={section.title}
            kind={section.kind}
            summary={section.summary}
            open={openKey === section.key}
            onToggle={() => toggle(section.key)}
          >
            {renderBody(section.key, {
              draft,
              firmOffer,
              isLoadingFirmOffer,
              readOnly,
            })}
          </ReadinessCard>
        ))}
      </div>
    );
  },
);

// ── Body dispatcher ─────────────────────────────────────────────────────────

function renderBody(key: SectionKey, p: Props) {
  switch (key) {
    case 'inherited':
      return <InheritedBody {...p} />;
    case 'pricing':
      return <PricingBody firmOffer={p.firmOffer} draft={p.draft} isLoading={p.isLoadingFirmOffer} />;
    case 'purchaseOrder':
      return <PurchaseOrderBody {...p} />;
    case 'shipping':
      return <ShippingBody {...p} />;
    case 'delivery':
      return <DeliveryBody {...p} />;
    case 'flags':
      return <FlagsBody {...p} />;
    default:
      return null;
  }
}

// ── Customer & Contract ─────────────────────────────────────────────────────
// Editable Customer (the Deliver/Bill-to company in the FO's hierarchy — drives
// the address book; the SO's entity & pricing stay from the FO) + editable Terms.
// The rest is read-only context carried from the Firm Offer.

function InheritedBody({ draft, firmOffer, isLoadingFirmOffer, readOnly }: Props) {
  const hierarchy = useSalesOrderStore((s) => s.customerHierarchy);
  const selectDoCustCompany = useSalesOrderStore((s) => s.selectDoCustCompany);
  const { control, setValue, reset, register } = useFormContext<SalesOrderFormData>();
  // useWatch (a tracked hook subscription) — NOT the watch() proxy. watch() reads RHF's
  // external mutable state the React Compiler can't track as a dependency, so it served
  // memoized stale JSX and these selects froze after the #357 RHF migration (86d3c5wwk).
  const doCustCompanyId = useWatch({ control, name: 'doCustCompanyId' });
  const doCustCompanyName = useWatch({ control, name: 'doCustCompanyName' });
  const termsId = useWatch({ control, name: 'termsId' });

  const companies =
    hierarchy.length > 0
      ? hierarchy
      : doCustCompanyId
        ? [{ id: doCustCompanyId, name: doCustCompanyName ?? doCustCompanyId, isParent: false }]
        : [];

  // Company change cascades in the store (address book + Terms/alerts/separate-billing
  // defaults). Run the cascade, then re-sync the WHOLE form from the cascaded store so
  // every dependent field (Terms, alerts, separateBilling, shipping/billing defaults)
  // reflects the new company — and the watch-sync can't clobber it (86d3afyp6).
  async function handleCompanySelect(id: string) {
    const name = companies.find((c) => c.id === id)?.name ?? '';
    setValue('doCustCompanyId', id, { shouldDirty: true });
    setValue('doCustCompanyName', name, { shouldDirty: true });
    // selectDoCustCompany now AWAITS the full cascade (profile defaults + address book) so
    // the store holds Terms/alerts/separateBilling before we re-seed (86d3c5wwk #1 — the
    // profile used to be fire-and-forget and landed after this reset). It returns false when
    // a newer pick superseded this one mid-cascade; skip the re-seed so we don't clobber the
    // newer company with this stale pick's snapshot (86d3c5wwk #3).
    const isLatest = await selectDoCustCompany(id, name);
    if (!isLatest) return;
    const d = useSalesOrderStore.getState().activeDraft;
    if (d) reset(draftToFormData(d));
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label>Customer</Label>
          <SearchableSelect
            options={companies.map((c) => ({
              id: c.id,
              name: c.name,
              suffix: c.isParent ? '(parent)' : undefined,
            }))}
            value={doCustCompanyId ?? null}
            onSelect={(id) => void handleCompanySelect(id)}
            placeholder="Select a company…"
            searchPlaceholder="Search companies…"
            disabled={readOnly || companies.length === 0}
          />
          <p className="text-[11px] text-muted-foreground">
            Pick from the parent company and its projects. Drives delivery &amp; billing; the order's pricing stays from the FO.
          </p>
        </div>

        <TermsSelect
          value={termsId}
          onChange={(id, name) => {
            setValue('termsId', id || null, { shouldDirty: true });
            setValue('termsName', name || null, { shouldDirty: true });
          }}
          readOnly={readOnly}
        />

        {/* Project — coordinator-editable + required at submit (86d3f81cz). Defaults from
            the FO; uncontrolled RHF input so keystrokes don't re-render the panel. The
            required gate lives in sectionStatus + the submit validator. */}
        <div className="space-y-1">
          <Label>
            Project <span className="text-destructive">*</span>
          </Label>
          <Input
            {...register('projectName')}
            placeholder="Enter the project"
            maxLength={500}
            readOnly={readOnly}
          />
        </div>
      </div>

      {firmOffer ? (
        <>
          <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
            <InfoField label="Head / Main Office" value={firmOffer.headMainOffice?.refName} />
            {/* Project moved to the editable grid above (now coordinator-editable + required, 86d3f81cz). */}
            {/* Read-only FO contract expiry. It defaults the editable PO Expiry Date in the
                Purchase Order & Documents group (86d3cxrjc) but is never edited here. */}
            <InfoField label="Contract Expiry" value={firmOffer.contractExpiryDate} />
            <InfoField label="Salesman" value={firmOffer.salesRep?.refName} />
            <InfoField label="Sales Coordinator" value={firmOffer.salesCoordinator?.refName} />
            <LinkField label="Form 2303" url={firmOffer.form2303LinkUrl} />
            <LinkField label="PO Link" url={firmOffer.poLinkUrl} />
          </div>
          <PerGradeBalanceChips firmOffer={firmOffer} lineItems={draft.lineItems} />
        </>
      ) : isLoadingFirmOffer ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-9 w-full" />
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          Contract details (office, project, salesman, freight) carry over once a Firm Offer is linked.
        </p>
      )}
    </div>
  );
}

// Editable Terms — loads the full cached NetSuite terms list and reports both id
// and name so the submit snapshot records what was chosen.
function TermsSelect({
  value,
  onChange,
  readOnly,
}: {
  value: string | null;
  onChange: (id: string, name: string) => void;
  readOnly?: boolean;
}) {
  const [terms, setTerms] = useState<Array<{ id: string; name: string }>>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    referenceDataService
      .getTerms()
      .then((rows) => {
        if (!cancelled) {
          setTerms(rows);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="space-y-1">
      <Label>Terms</Label>
      <SearchableSelect
        options={terms}
        value={value}
        onSelect={(id) => onChange(id, terms.find((t) => t.id === id)?.name ?? '')}
        placeholder="Select terms…"
        searchPlaceholder="Search terms…"
        disabled={readOnly}
        loading={loading}
      />
    </div>
  );
}

// Compact read-only label/value for carried-over FO context.
function InfoField({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className="font-medium">{value || <span className="text-muted-foreground">—</span>}</span>
    </div>
  );
}

function LinkField({ label, url }: { label: string; url: string | null | undefined }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
      {url ? (
        <a href={url} target="_blank" rel="noopener noreferrer" className="text-info hover:underline">
          Open
        </a>
      ) : (
        <span className="text-muted-foreground">—</span>
      )}
    </div>
  );
}

// ── Pricing (🔒, FO-locked) ─────────────────────────────────────────────────

const PRICING_GRADES: Array<{
  key: keyof FirmOfferDetailDto['pricing']['perGrade'];
  pns: string;
  label: string;
}> = [
  { key: 'grade230', pns: '230', label: 'G33 (230)' },
  { key: 'grade280', pns: '280', label: 'G40 (280)' },
  { key: 'grade420', pns: '420', label: 'G60 (420)' },
  { key: 'grade520', pns: '520', label: 'G75 (520)' },
  { key: 'grade550', pns: '550', label: 'G80 (550)' },
];

const PHP_FMT = new Intl.NumberFormat('en-PH', {
  style: 'currency',
  currency: 'PHP',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const fmtPhp = (n: number) => PHP_FMT.format(n);
const fmtKg = (n: number) => n.toLocaleString('en-PH', { maximumFractionDigits: 2 });
const fmtMt = (n: number) => kgToMt(n).toLocaleString('en-PH', { maximumFractionDigits: 3 });
const fmtQty = (n: number) => n.toLocaleString('en-PH');

// The Pricing card: FO-locked per-grade markup/price (left, immutable) enriched with the
// current draft's rebar weight / qty / estimated value (right) plus order-level estimated
// totals. Estimate only — NetSuite computes the authoritative totals on submit, and freight
// (its charge + Freight12% tax) is excluded here (owned by the freight work, ticket 86d39jbhk).
function PricingBody({
  firmOffer,
  draft,
  isLoading,
}: {
  firmOffer: FirmOfferDetailDto | null;
  draft: SalesOrderDto;
  isLoading?: boolean;
}) {
  if (!firmOffer) {
    if (isLoading) {
      return (
        <div className="space-y-2">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      );
    }
    return <p className="text-sm text-muted-foreground">Pricing carries over from the Firm Offer.</p>;
  }
  const { pricing } = firmOffer;
  const summary = aggregateRebarPricing(draft.lineItems, firmOffer);
  const byPns = new Map(summary.perGrade.map((g) => [g.pns, g]));
  const hasLines = summary.lineCount > 0;
  const zeroRated = summary.taxRate === 0;
  const vatLabel = zeroRated
    ? 'Est. VAT (0% · zero-rated)'
    : `Est. VAT (${(summary.taxRate * 100).toFixed(0)}%)`;

  // Freight + combined order total (ticket 86d39jbhk). Same shared utils as the Line
  // Items footer block, so the combined figure is identical in both surfaces.
  const freight = computeSoFreight({
    totalWeightKg: summary.totalWeightKg,
    // 86d3fteqa: editable Freight/Ton drives the charge; defaults from the FO rate.
    freightPerTon: draft.freightChargePerTon ?? firmOffer.freightChargePerTon,
    waived: draft.waivedFreight === '1',
  });
  const combined = combinedOrderTotal(summary.grandTotal, freight.charge);

  return (
    <div className="space-y-3">
      {/* Summary strip — order-level rebar context from the current line items. */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <PricingStat
          label="Total Weight"
          value={hasLines ? `${fmtMt(summary.totalWeightKg)} MT` : '—'}
          sub={hasLines ? `${fmtKg(summary.totalWeightKg)} kg` : undefined}
        />
        <PricingStat label="Total Qty" value={hasLines ? `${fmtQty(summary.totalQty)} pcs` : '—'} />
        <PricingStat label="Line Items" value={fmtQty(summary.lineCount)} />
        <PricingStat
          label="Overall ₱/kg"
          value={pricing.overallPricePerKg != null ? fmtPhp(pricing.overallPricePerKg) : '—'}
        />
      </div>

      {/* Per-grade: FO-locked markup/price + draft weight/qty/est-value. */}
      <div className="overflow-hidden rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-3 py-1.5 text-left font-medium">Grade</th>
              <th className="px-3 py-1.5 text-right font-medium">Markup %</th>
              <th className="px-3 py-1.5 text-right font-medium">Price / kg</th>
              <th className="px-3 py-1.5 text-right font-medium">Weight (MT)</th>
              <th className="px-3 py-1.5 text-right font-medium">Qty</th>
              <th className="px-3 py-1.5 text-right font-medium">Est. value</th>
            </tr>
          </thead>
          <tbody>
            {PRICING_GRADES.map(({ key, pns, label }) => {
              const g = pricing.perGrade[key];
              const agg = byPns.get(pns);
              const active = agg != null && (agg.weightKg > 0 || agg.qty > 0);
              return (
                <tr key={key} className={cn('border-t', !active && 'text-muted-foreground')}>
                  <td className={cn('px-3 py-1.5', active && 'font-medium text-foreground')}>{label}</td>
                  <td className="px-3 py-1.5 text-right font-mono tabular-nums">
                    {g.markupPct.toFixed(2)}%
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono tabular-nums">
                    {fmtPhp(g.pricePerKg)}
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono tabular-nums">
                    {active ? fmtMt(agg!.weightKg) : '—'}
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono tabular-nums">
                    {active ? fmtQty(agg!.qty) : '—'}
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono tabular-nums">
                    {active ? fmtPhp(agg!.estValue) : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
          {hasLines && (
            <tfoot className="border-t bg-muted/30 text-xs">
              <tr className="font-medium">
                <td className="px-3 py-1.5" colSpan={3}>
                  Total
                </td>
                <td className="px-3 py-1.5 text-right font-mono tabular-nums">
                  {fmtMt(summary.totalWeightKg)}
                </td>
                <td className="px-3 py-1.5 text-right font-mono tabular-nums">
                  {fmtQty(summary.totalQty)}
                </td>
                <td className="px-3 py-1.5 text-right font-mono tabular-nums">
                  {fmtPhp(summary.subtotal)}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Estimated order totals — rebar + freight, preview. */}
      {hasLines && (
        <div className="rounded-md border bg-muted/20 px-3 py-2 text-sm">
          <EstRow label="Est. subtotal (ex-VAT)" value={fmtPhp(summary.subtotal)} />
          <EstRow label={vatLabel} value={fmtPhp(summary.vat)} />
          <EstRow label="Rebar grand total" value={fmtPhp(summary.grandTotal)} />
          <EstRow
            label={freight.waived ? 'Freight (waived)' : 'Freight (VAT-incl.)'}
            value={freight.waived ? fmtPhp(0) : freight.charge != null ? fmtPhp(freight.charge) : '—'}
          />
          <EstRow label="Est. order total" value={fmtPhp(combined)} strong />
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        Markup &amp; price/kg are locked from the Firm Offer. Weight, quantity and value are an{' '}
        <span className="font-medium">estimate from the current line items</span> — NetSuite computes
        the final totals on submit. Freight uses the FO&apos;s VAT-inclusive per-ton rate and is
        included in the order total.
      </p>
    </div>
  );
}

// Summary-strip stat cell.
function PricingStat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-md border bg-card px-3 py-2">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="font-mono text-sm font-semibold tabular-nums">{value}</div>
      {sub && <div className="font-mono text-xs text-muted-foreground tabular-nums">{sub}</div>}
    </div>
  );
}

// Estimated-totals row.
function EstRow({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div
      className={cn(
        'flex items-baseline justify-between gap-2 py-0.5',
        strong && 'mt-0.5 border-t pt-1.5',
      )}
    >
      <span className={cn('text-muted-foreground', strong && 'font-medium text-foreground')}>
        {label}
      </span>
      <span className={cn('font-mono tabular-nums', strong && 'font-semibold')}>{value}</span>
    </div>
  );
}

// ── Purchase Order & Documents ──────────────────────────────────────────────

function PurchaseOrderBody({ readOnly }: Props) {
  const { register, control, setValue, formState } = useFormContext<SalesOrderFormData>();

  // 86d3cxrjc: PO Expiry Date defaults from the FO's contract expiry while it is still
  // blank and the coordinator hasn't touched it; once set (defaulted or typed) it is
  // theirs to edit. useWatch (not the watch() proxy) for a compiler-safe subscription
  // (86d3c5wwk).
  const poExpiry = useWatch({ control, name: 'poExpiryDate' });
  const foContractExpiry = useSalesOrderStore(
    (s) => s.selectedFirmOffer?.contractExpiryDate ?? null,
  );
  useEffect(() => {
    if (readOnly || !foContractExpiry) return;
    if (poExpiry) return; // already has a value — leave it
    if (formState.dirtyFields.poExpiryDate) return; // user cleared/edited it — respect that
    setValue('poExpiryDate', foContractExpiry, { shouldDirty: false });
    // formState/setValue read fresh inside; trigger is FO/PO availability.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poExpiry, foContractExpiry, readOnly]);

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <div className="space-y-1.5">
        <Label>
          Customer PO # <span className="text-destructive">*</span>
        </Label>
        <Input
          {...register('customerPoNumber')}
          placeholder="Enter the customer PO #"
          maxLength={64}
          readOnly={readOnly}
        />
        {/* 86d39w9rn — informational; does not relax the required PO # above */}
        <Controller
          name="waitingForOfficialPo"
          control={control}
          render={({ field }) => (
            <FlagCheckbox
              label="Waiting for Official PO"
              checked={field.value}
              onChange={field.onChange}
              readOnly={readOnly}
            />
          )}
        />
      </div>
      <div className="space-y-1">
        <Label>
          PO Expiry Date <span className="text-destructive">*</span>
        </Label>
        <Input type="date" {...register('poExpiryDate')} readOnly={readOnly} />
        <p className="text-[11px] text-muted-foreground">
          Defaults to the FO contract expiry; edit to override.
        </p>
      </div>
      <div className="space-y-1">
        <Label>Sub-PO / Orderlist Link</Label>
        <Input {...register('subPoOrderlistLink')} placeholder="https://…" readOnly={readOnly} />
      </div>
      <div className="space-y-1">
        <Label>Invoice To Letter</Label>
        <Input {...register('invoiceToLetter')} readOnly={readOnly} />
      </div>
    </div>
  );
}

// ── Freight-billing awareness (86d3addwy) ───────────────────────────────────
// Shown only when the Deliver/Bill-to company is set to Separate Billing or Freight
// Incorporated. Lets the coordinator confirm whether the FREIGHT line is posted on
// this SO, with the omit consequence spelled out. Writes draft.includeFreightLine —
// NOT waivedFreight (a distinct concession). Soft awareness only; never blocks submit.
function FreightBillingCallout({
  companyName,
  freightCharge,
  readOnly,
}: {
  companyName: string | null;
  freightCharge: number | null;
  readOnly?: boolean;
}) {
  const { control, setValue } = useFormContext<SalesOrderFormData>();
  // useWatch, not watch() — compiler-safe subscription (86d3c5wwk; see InheritedBody).
  const separateBilling = useWatch({ control, name: 'separateBilling' });
  const includeFreightLine = useWatch({ control, name: 'includeFreightLine' });
  const waivedFreight = useWatch({ control, name: 'waivedFreight' });
  if (!freightBillingNeedsPrompt(separateBilling)) return null;
  // 86d3hm2vr: a waived-freight order needs no freight-billing disposition prompt — the
  // order carries no freight regardless. Suppress the callout to avoid a misleading alert.
  if (waivedFreight === '1') return null;

  const isSeparate = separateBilling === SeparateBilling.Yes;
  const included = resolveIncludeFreightLine(separateBilling, includeFreightLine);
  const confirmed = includeFreightLine != null;
  const who = companyName || 'This customer';
  // Only show the peso amount once freight is computable (has line items + a rate);
  // otherwise drop it entirely rather than printing a placeholder.
  const amount = freightCharge != null ? fmtPhp(freightCharge) : null;
  const amountPrefix = amount ? `${amount} ` : '';
  const heading = isSeparate
    ? `${who} bills freight separately.`
    : `Freight is incorporated into the item price for ${who}.`;

  return (
    <div className="rounded-md border border-warning/40 bg-warning/10 p-3">
      <div className="flex items-start gap-2">
        <Truck className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
        <div className="space-y-2.5">
          <div>
            <p className="text-sm font-medium text-foreground">{heading}</p>
            <p className="text-xs text-muted-foreground">
              {confirmed
                ? included
                  ? 'The freight line is included on this Sales Order.'
                  : 'This Sales Order will have NO freight line item.'
                : 'Confirm how this order handles the freight charge.'}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant={included ? 'default' : 'outline'}
              disabled={readOnly}
              onClick={() => setValue('includeFreightLine', true, { shouldDirty: true })}
            >
              {amount ? `Include freight line (${amount})` : 'Include freight line'}
            </Button>
            <Button
              type="button"
              size="sm"
              variant={!included ? 'default' : 'outline'}
              disabled={readOnly}
              onClick={() => setValue('includeFreightLine', false, { shouldDirty: true })}
            >
              Omit — no freight line
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            {included
              ? isSeparate
                ? `Adds the ${amountPrefix}FREIGHT CHARGE line to this Sales Order.`
                : `Adds the ${amountPrefix}FREIGHT CHARGE line. ⚠ May double-charge if freight is already baked into the item price.`
              : isSeparate
                ? 'No freight line item — freight is billed on a separate document; ₱0 freight on this SO.'
                : 'No freight line item — freight is already included in the item price.'}
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Shipping & Billing ──────────────────────────────────────────────────────
// Address selects are driven by the Customer picked in the Customer & Contract
// section (its address book + defaults). Here: state/town, shipping + billing
// address, the freight fields, read-only Tax Num, and the editable Document Alerts.

function ShippingBody({ draft, firmOffer, readOnly }: Props) {
  const { register, control, setValue } = useFormContext<SalesOrderFormData>();
  const profile = useSalesOrderStore((s) => s.companyProfile);
  const addresses = useSalesOrderStore((s) => s.customerAddresses);

  // Shipping address: map the form's flat fields ↔ the picker's discriminated
  // ShippingAddressInput (86d3afyp6 — replaces the panel-local shippingValue adapter).
  // State/Town are owned by the separate comboboxes above, so they're left untouched here.
  // useWatch, not watch() — compiler-safe subscription (86d3c5wwk; see InheritedBody).
  const sAddrId = useWatch({ control, name: 'shippingAddressId' });
  const sAddressee = useWatch({ control, name: 'shippingAddresseeName' });
  const sLine1 = useWatch({ control, name: 'shippingAddrLine1' });
  const sLine2 = useWatch({ control, name: 'shippingAddrLine2' });
  const sLine3 = useWatch({ control, name: 'shippingAddrLine3' });
  const sZip = useWatch({ control, name: 'shippingZip' });
  const sCountry = useWatch({ control, name: 'shippingCountryId' });
  // 86d405z28 — the add-address dialog covers the two comboboxes above, so it shows their
  // values in its context strip and seeds City + the Province suggestion from them.
  // useWatch (not watch()) so the compiler tracks the subscription.
  const shippingStateProvinceId = useWatch({ control, name: 'shippingStateProvinceId' });
  const shippingCityTownId = useWatch({ control, name: 'shippingCityTownId' });
  const shippingAddress: ShippingAddressInput = sAddrId
    ? { savedAddressId: sAddrId, custom: null }
    : sAddressee || sLine1
      ? {
          savedAddressId: null,
          custom: {
            addressee: sAddressee ?? '',
            addrLine1: sLine1 ?? '',
            addrLine2: sLine2,
            addrLine3: sLine3,
            cityTownId: null,
            stateProvinceId: null,
            zip: sZip,
            countryId: sCountry ?? 'PH',
            saveToCustomer: false,
          },
        }
      : { savedAddressId: null, custom: null };

  function onShippingAddressChange(next: ShippingAddressInput) {
    const opt = { shouldDirty: true } as const;
    if (next.savedAddressId) {
      setValue('shippingAddressId', next.savedAddressId, opt);
      setValue('shippingAddresseeName', null, opt);
      setValue('shippingAddrLine1', null, opt);
      setValue('shippingAddrLine2', null, opt);
      setValue('shippingAddrLine3', null, opt);
      setValue('shippingAddressOverrideJson', null, opt);
    } else if (next.custom) {
      const c = next.custom;
      setValue('shippingAddressId', null, opt);
      setValue('shippingAddresseeName', c.addressee, opt);
      setValue('shippingAddrLine1', c.addrLine1, opt);
      setValue('shippingAddrLine2', c.addrLine2, opt);
      setValue('shippingAddrLine3', c.addrLine3, opt);
      setValue('shippingZip', c.zip, opt);
      setValue('shippingCountryId', c.countryId, opt);
      setValue(
        'shippingAddressOverrideJson',
        JSON.stringify({
          override: true,
          addressee: c.addressee,
          addrLine1: c.addrLine1,
          addrLine2: c.addrLine2,
          addrLine3: c.addrLine3,
          countryId: c.countryId,
        }),
        opt,
      );
    } else {
      setValue('shippingAddressId', null, opt);
      setValue('shippingAddresseeName', null, opt);
      setValue('shippingAddrLine1', null, opt);
      setValue('shippingAddrLine2', null, opt);
      setValue('shippingAddrLine3', null, opt);
      setValue('shippingAddressOverrideJson', null, opt);
    }
  }

  const billingAddressId = useWatch({ control, name: 'billingAddressId' });

  // Compact freight charge for the summary line below the freight row (ticket 86d39jbhk).
  // Shares computeSoFreight with the Line Items footer block + Pricing panel.
  const freight = computeSoFreight({
    totalWeightKg: totalLineWeightKg(draft.lineItems),
    // 86d3fteqa: editable Freight/Ton drives the charge; defaults from the FO rate.
    freightPerTon: draft.freightChargePerTon ?? firmOffer?.freightChargePerTon ?? null,
    waived: draft.waivedFreight === '1',
  });
  // Whether the FREIGHT line is posted on this SO (86d3addwy). When omitted (separate
  // billing / incorporated), the computed freight is excluded from the order.
  const freightLineIncluded = resolveIncludeFreightLine(
    draft.separateBilling,
    draft.includeFreightLine,
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Controller
          name="shippingStateProvinceId"
          control={control}
          render={({ field }) => (
            <ReferenceDataCombobox
              source="stateProvinces"
              label="State / Province"
              value={field.value}
              onChange={field.onChange}
              required
              disabled={readOnly}
            />
          )}
        />
        <Controller
          name="shippingCityTownId"
          control={control}
          render={({ field }) => (
            <ReferenceDataCombobox
              source="cityTowns"
              label="City / Town"
              value={field.value}
              onChange={field.onChange}
              required
              disabled={readOnly}
            />
          )}
        />
      </div>

      <div className="grid grid-cols-1 items-start gap-3 sm:grid-cols-2">
        <ShippingAddressPicker
          value={shippingAddress}
          onChange={onShippingAddressChange}
          readOnly={readOnly}
          // 86d405z28 — the picker now writes to the customer's NetSuite address book, so it
          // needs to know WHOSE book. Addresses hang off the deliver-to company (custbody_csc_do_cust),
          // which is what loadCustomerAddresses is keyed on — not the FO's billing customer.
          customerId={draft.doCustCompanyId ?? draft.customerId ?? null}
          customerName={draft.doCustCompanyName ?? draft.customerName ?? 'this customer'}
          stateProvinceId={shippingStateProvinceId ?? null}
          cityTownId={shippingCityTownId ?? null}
        />

        {/* Billing address — saved-pick select from the same company's address book.
            Same AddressCard shell as the Shipping column so both read identically. */}
        <AddressCard
          icon={Receipt}
          title="Billing Address"
          control={
            <Select
              value={billingAddressId ?? undefined}
              onValueChange={(id) => {
                const opt = { shouldDirty: true } as const;
                setValue('billingAddressId', id, opt);
                setValue('billingAddresseeName', null, opt);
                setValue('billingAddrLine1', null, opt);
                setValue('billingAddrLine2', null, opt);
                setValue('billingAddrLine3', null, opt);
                setValue('billingAddressOverrideJson', null, opt);
              }}
              disabled={readOnly}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select billing address…" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {addresses.map((a) => (
                  <SelectItem key={a.internalId} value={a.internalId}>
                    {a.label || a.addressee || a.addrText?.slice(0, 60) || a.internalId}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          }
          detail={
            billingAddressId ? (
              <AddressPreview address={addresses.find((a) => a.internalId === billingAddressId)} />
            ) : (
              <span className="text-sm text-muted-foreground">No address selected</span>
            )
          }
        />
      </div>

      {/* Freight — one row on desktop (Freight/Ton drives the charge · actual ref-only · waived · discounted · details) */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
        {/* 86d3fteqa: editable rate that DRIVES the freight charge (was FO-locked). Seeded from
            the FO at draft-save; coordinator may override. */}
        <div className="space-y-1">
          <Label>Freight / Ton</Label>
          <Input
            type="number"
            {...register('freightChargePerTon', {
              setValueAs: (v) => (v === '' || v == null ? null : Number(v)),
            })}
            readOnly={readOnly}
          />
          <p className="text-xs text-muted-foreground">Defaults from FO, editable</p>
        </div>
        {/* 86d3fteqa: informational only — does NOT affect the freight computation. */}
        <div className="space-y-1">
          <Label>Actual Freight / MT</Label>
          <Input
            type="number"
            {...register('actualFreightPerMt', {
              setValueAs: (v) => (v === '' || v == null ? null : Number(v)),
            })}
            readOnly={readOnly}
          />
          <p className="text-xs text-muted-foreground">Reference only — does not affect freight</p>
        </div>
        <div className="space-y-1">
          <Label>
            Waived Freight <span className="text-destructive">*</span>
          </Label>
          <Controller
            name="waivedFreight"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value || undefined}
                onValueChange={field.onChange}
                disabled={readOnly}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select…" />
                </SelectTrigger>
                <SelectContent>
                  {WAIVED_FREIGHT_OPTIONS.map((opt) => (
                    <SelectItem key={opt.id} value={opt.id}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>
        <div className="space-y-1">
          <Label>Discounted Freight</Label>
          <Controller
            name="isDiscountedFreight"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value ? 'yes' : 'no'}
                onValueChange={(v) => field.onChange(v === 'yes')}
                disabled={readOnly}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="no">No</SelectItem>
                  <SelectItem value="yes">Yes</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        </div>
        <div className="space-y-1">
          <Label>Discount Details</Label>
          <Input {...register('discountDetails')} readOnly={readOnly} />
        </div>
      </div>

      {/* Freight-billing awareness (86d3addwy) — only when the Deliver/Bill-to company is
          set to Separate Billing / Freight Incorporated; lets the coordinator confirm
          whether the freight line is posted on this SO. */}
      <FreightBillingCallout
        companyName={draft.doCustCompanyName}
        freightCharge={freight.waived ? 0 : freight.charge}
        readOnly={readOnly}
      />

      {/* Compact computed-freight summary — same value as the Line Items footer block. */}
      <p className="text-xs text-muted-foreground">
        Computed freight:{' '}
        <span className="font-mono font-semibold text-foreground">
          {!freightLineIncluded
            ? fmtPhp(0)
            : freight.tons <= 0
              ? '—'
              : freight.waived
                ? fmtPhp(0)
                : freight.charge != null
                  ? fmtPhp(freight.charge)
                  : '—'}
        </span>
        {!freightLineIncluded
          ? ' · omitted (no freight line)'
          : freight.tons <= 0
            ? ' · pending line items'
            : freight.waived
              ? ' · waived'
              : freight.rate != null
                ? ' · rate / ton'
                : ' · no rate yet'}
        {freightLineIncluded && freight.tons > 0 && !freight.waived && draft.isDiscountedFreight
          ? ' · discounted'
          : ''}
      </p>

      {/* Tax Num — read-only, derived from the selected customer */}
      <div className="sm:max-w-xs">
        <ReadOnlyField label="Tax Num" value={profile?.taxNum} />
      </div>

      <DocumentAlerts readOnly={readOnly} />
    </div>
  );
}

// Read-only customer-derived field (Tax Num, FO Freight/Ton) — visibly locked.
// Renders an actual disabled <Input> so it's pixel-identical to the editable
// inputs/selects beside it (same h-9, border, padding, shadow) — no look-alike div.
function ReadOnlyField({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="space-y-1">
      <Label>
        {label}
        <Lock className="ml-1 inline h-3 w-3 align-middle text-muted-foreground" />
      </Label>
      <Input
        value={value ?? ''}
        placeholder="—"
        readOnly
        tabIndex={-1}
        className="bg-muted/40"
      />
    </div>
  );
}

// Document Alerts — editable on the SO (default from the customer; written to the
// transaction custbody_* fields on submit).
type AlertField =
  | 'alertInvoiceECopy'
  | 'alertManualComputation'
  | 'alertMc2Copies'
  | 'alertDrPicture'
  | 'alertDrAttachment'
  | 'alertBrnTraceability';

const DOCUMENT_ALERTS: Array<{ field: AlertField; label: string }> = [
  { field: 'alertInvoiceECopy', label: 'E-Copy Alert' },
  { field: 'alertManualComputation', label: 'Manual Alert' },
  { field: 'alertMc2Copies', label: '2 Copies MC' },
  { field: 'alertDrPicture', label: 'DR Alert (Picture)' },
  { field: 'alertDrAttachment', label: 'DR Alert (Attachment)' },
  { field: 'alertBrnTraceability', label: 'BRN Traceability' },
];

function DocumentAlerts({ readOnly }: { readOnly?: boolean }) {
  const { control } = useFormContext<SalesOrderFormData>();
  return (
    <div className="space-y-2">
      <Label>Document Alerts</Label>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {DOCUMENT_ALERTS.map(({ field, label }) => (
          <Controller
            key={field}
            name={field}
            control={control}
            render={({ field: f }) => (
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={Boolean(f.value)}
                  onCheckedChange={(v) => !readOnly && f.onChange(v === true)}
                  disabled={readOnly}
                />
                {label}
              </label>
            )}
          />
        ))}
      </div>
    </div>
  );
}

// ── Delivery Requirements ───────────────────────────────────────────────────

function DeliveryBody({ readOnly }: Props) {
  const { register, control, setValue } = useFormContext<SalesOrderFormData>();
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label>
            Delivery Due Date <span className="text-destructive">*</span>
          </Label>
          <Input type="date" {...register('deliveryDueDate')} readOnly={readOnly} />
        </div>
        <Controller
          name="scheduleId"
          control={control}
          render={({ field }) => (
            <ReferenceDataCombobox
              source="schedules"
              label="Schedule"
              value={field.value}
              onChange={(id, name) => {
                field.onChange(id);
                setValue('scheduleName', name ?? null, { shouldDirty: true });
              }}
              disabled={readOnly}
            />
          )}
        />
      </div>

      <OfficeContacts readOnly={readOnly} />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label>Authorized Receivers</Label>
          <Textarea
            {...register('authorizedReceivers')}
            rows={3}
            placeholder="Name + mobile per line"
            readOnly={readOnly}
          />
        </div>
        <div className="space-y-1">
          <Label>Other Instructions</Label>
          <Textarea {...register('otherInstructions')} rows={3} readOnly={readOnly} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <ControlledFlag name="isTrailer" label="Trailer" control={control} readOnly={readOnly} />
        <ControlledFlag name="isMf" label="M-F" control={control} readOnly={readOnly} />
        <ControlledFlag name="isPpeRequired" label="PPE Required" control={control} readOnly={readOnly} />
        <ControlledFlag name="isPermitRequired" label="Permit Required" control={control} readOnly={readOnly} />
      </div>
    </div>
  );
}

// Office contacts: show #1 (required) by default, "+ Add" reveals #2 then #3,
// "✕" removes a slot and re-packs the rest up (positional 1→3 mapping).
const CONTACT_FIELDS = [
  { name: 'officeContact1', num: 'contactNumber1' },
  { name: 'officeContact2', num: 'contactNumber2' },
  { name: 'officeContact3', num: 'contactNumber3' },
] as const;

function OfficeContacts({ readOnly }: { readOnly?: boolean }) {
  const { register, getValues, setValue } = useFormContext<SalesOrderFormData>();
  // Seed visible count from how many slots have data at mount (the form was reset
  // from the draft before this mounts). 86d3afyp6.
  const filledCount = CONTACT_FIELDS.filter(
    (f) => getValues(f.name) || getValues(f.num),
  ).length;
  const [visibleCount, setVisibleCount] = useState(Math.max(1, filledCount));

  function removePair(index: number) {
    // Drop the slot and re-pack later contacts up so 1→3 stay contiguous.
    const pairs = CONTACT_FIELDS.map((f) => ({
      name: getValues(f.name),
      num: getValues(f.num),
    }));
    pairs.splice(index, 1);
    pairs.push({ name: null, num: null });
    CONTACT_FIELDS.forEach((f, i) => {
      setValue(f.name, pairs[i].name ?? null, { shouldDirty: true });
      setValue(f.num, pairs[i].num ?? null, { shouldDirty: true });
    });
    setVisibleCount((c) => Math.max(1, c - 1));
  }

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {Array.from({ length: visibleCount }).map((_, i) => (
          <div key={i} className="space-y-1">
            <div className="flex items-center justify-between">
              <Label>
                Office Contact {i + 1}
                {i === 0 && <span className="text-destructive"> *</span>}
              </Label>
              {i > 0 && !readOnly && (
                <button
                  type="button"
                  onClick={() => removePair(i)}
                  className="text-muted-foreground hover:text-destructive"
                  aria-label={`Remove office contact ${i + 1}`}
                  title="Remove"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <Input {...register(CONTACT_FIELDS[i].name)} placeholder="Name" readOnly={readOnly} />
            <Input {...register(CONTACT_FIELDS[i].num)} placeholder="Mobile #" readOnly={readOnly} />
          </div>
        ))}
      </div>
      {visibleCount < 3 && !readOnly && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 gap-1.5 text-xs"
          onClick={() => setVisibleCount((c) => Math.min(3, c + 1))}
        >
          <Plus className="h-3.5 w-3.5" />
          Add contact
        </Button>
      )}
    </div>
  );
}

// ── Flags & Notes ───────────────────────────────────────────────────────────

function FlagsBody({ readOnly }: Props) {
  const { register, control } = useFormContext<SalesOrderFormData>();
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <ControlledFlag name="isPriority" label="Priority" control={control} readOnly={readOnly} />
        <ControlledFlag name="isSpotOrder" label="Spot Order" control={control} readOnly={readOnly} />
        <ControlledFlag name="isReprint" label="Reprint" control={control} readOnly={readOnly} />
      </div>
      <div className="space-y-1">
        <Label>Other Remarks (defaults from FO memo)</Label>
        <Textarea {...register('soRemarks')} rows={2} readOnly={readOnly} />
      </div>
    </div>
  );
}

// ── Shared field atoms ──────────────────────────────────────────────────────

function FlagCheckbox({
  label,
  checked,
  onChange,
  readOnly,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  readOnly?: boolean;
}) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <Checkbox
        checked={checked}
        onCheckedChange={(v) => !readOnly && onChange(v === true)}
        disabled={readOnly}
      />
      {label}
    </label>
  );
}

// RHF-bound flag checkbox — Controller wrapper around FlagCheckbox (86d3afyp6).
function ControlledFlag({
  name,
  label,
  control,
  readOnly,
}: {
  name: BooleanFieldName;
  label: string;
  control: Control<SalesOrderFormData>;
  readOnly?: boolean;
}) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <FlagCheckbox
          label={label}
          checked={Boolean(field.value)}
          onChange={field.onChange}
          readOnly={readOnly}
        />
      )}
    />
  );
}
